import SelectComponent from "@via/components/select/select.tsx";
import {
  ExecutorJobOptions,
  ExecutorJobsExternals,
  ExecutorJobsLocals,
  ExecutorOptions,
  executorSelectValue,
} from "@via/pages/account/profile/run-environments/run-environment-form/run-environment-constants.ts";
import {
  ExecutorExternals,
  ExecutorJobsExternal,
  ExecutorJobsLocal,
  ExecutorLocal,
  MaximumExecutorJobLimit,
} from "@via/pages/account/profile/run-environments/run-environment-form/run-environment-form-helpers.tsx";
import {
  ExecutorJobsType,
  ExecutorType,
  type ProfileClusterForClient,
} from "common";
import { type JSX } from "react";
import { type UseFormRegister } from "react-hook-form";

export const executionCardSummary = (params: {
  selectedExecutor: ExecutorType;
  selectedExecutorJobs: ExecutorJobsType;
}): string => {
  const executor =
    executorSelectValue(params.selectedExecutor)?.label ??
    params.selectedExecutor;
  const jobs =
    ExecutorJobOptions.find(o => o.value === params.selectedExecutorJobs)
      ?.label ?? params.selectedExecutorJobs;
  return `${executor} / ${jobs}`;
};

export function ExecutionCard({
  register,
  selectedExecutor,
  onExecutorChange,
  selectedExecutorJobs,
  onExecutorJobsChange,
  isExecutorJobsSelectorDisabled,
}: {
  register: UseFormRegister<ProfileClusterForClient>;
  selectedExecutor: ExecutorType;
  onExecutorChange: (option: any) => void;
  selectedExecutorJobs: ExecutorJobsType;
  onExecutorJobsChange: (option: any) => void;
  isExecutorJobsSelectorDisabled: boolean;
}): JSX.Element {
  return (
    <>
      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Executor of Nextflow
        </label>
        <div className="my-2" data-testid="executor">
          <SelectComponent
            name="executor"
            onChange={onExecutorChange}
            value={executorSelectValue(selectedExecutor)}
            options={ExecutorOptions}
            enableClear={false}
          />
        </div>

        <div className="mt-4 p-4 shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg">
          <label className="block text-sm font-medium leading-6 text-gray-900">
            Executor Settings for Nextflow
          </label>
          {selectedExecutor === ExecutorType.Local ? (
            <ExecutorLocal register={register} />
          ) : (
            <ExecutorExternals
              register={register}
              selectedExecutor={selectedExecutor}
            />
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Executor of Nextflow Jobs
        </label>
        <div className="my-2" data-testid="executor-job">
          <SelectComponent
            name="executorJob"
            onChange={onExecutorJobsChange}
            readOnly={isExecutorJobsSelectorDisabled}
            value={ExecutorJobOptions.find(
              ({ value }) => value === selectedExecutorJobs
            )}
            options={ExecutorJobOptions}
            enableClear={false}
          />
        </div>

        <div className="mt-4 p-4 shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg">
          {ExecutorJobsLocals.includes(selectedExecutorJobs) ? (
            <ExecutorJobsLocal
              register={register}
              selectedExecutorJobs={selectedExecutorJobs}
            />
          ) : ExecutorJobsExternals.includes(selectedExecutorJobs) ? (
            <ExecutorJobsExternal
              register={register}
              selectedExecutorJobs={selectedExecutorJobs}
            />
          ) : null}
        </div>
      </div>

      <div>
        <div className="mt-4 p-4 shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg">
          <MaximumExecutorJobLimit register={register} />
        </div>
      </div>
    </>
  );
}
